// Shared billing display/behaviour helpers for both portals — one source of
// truth for status→badge mappings, adjustment/forecast labels and the invoice
// HTML viewer.
import { moduleDefinition } from "@cms/config";
import type { BillingAdjustment, BillingCadence } from "@cms/types";
import type { ApiClient } from "./api";
import { formatPence } from "./format";

type BadgeVariant = "default" | "secondary" | "destructive" | "outline" | "success" | "warning";

/** tenants.billing_status → Badge variant. */
export function billingStatusBadgeVariant(status: string | null | undefined): BadgeVariant {
  switch (status) {
    case "active":
      return "success";
    case "mandate_active":
      return "default";
    case "past_due":
      return "warning";
    case "suspended":
    case "cancelled":
      return "destructive";
    default:
      return "secondary";
  }
}

/** Invoice status → Badge variant. */
export function invoiceStatusBadgeVariant(status: string | null | undefined): BadgeVariant {
  switch (status) {
    case "paid":
      return "success";
    case "pending":
      return "warning";
    case "failed":
      return "destructive";
    default:
      return "secondary";
  }
}

/** Human labels for billing statuses (client-facing copy, not raw enum). */
export const BILLING_STATUS_LABELS: Record<string, string> = {
  draft: "Not set up",
  mandate_active: "Direct Debit ready",
  active: "Active",
  past_due: "Payment overdue",
  suspended: "Suspended",
  cancelled: "Cancelled",
};

export function billingStatusLabel(status: string | null | undefined): string {
  return (status && BILLING_STATUS_LABELS[status]) || status || "Unknown";
}

// --- Billing cadence ---

/** "Monthly" / "Annual" — for badges and selectors. */
export function cadenceLabel(cadence: BillingCadence | null | undefined): string {
  return cadence === "annual" ? "Annual" : "Monthly";
}

/** "month" / "year" — the period one charge covers, for "£X per month" copy. */
export function cadencePeriodNoun(cadence: BillingCadence | null | undefined): string {
  return cadence === "annual" ? "year" : "month";
}

/** "/month" / "/year" — compact amount suffix. */
export function cadenceSuffix(cadence: BillingCadence | null | undefined): string {
  return `/${cadencePeriodNoun(cadence)}`;
}

// --- Forecast & recurring adjustments ---

/** The forecast's global banner — rolling months track future standard pricing. */
export const ROLLING_ESTIMATE_BANNER =
  "Rolling-phase amounts are estimates based on today's standard pricing and will follow any future changes to standard prices. Fixed-term amounts are locked.";

/** Which line an adjustment targets, e.g. "Plan" or "Bookings module". */
export function adjustmentTargetLabel(adj: BillingAdjustment): string {
  if (adj.line_kind === "plan") return "Plan";
  const def = moduleDefinition(adj.module_slug);
  return `${def?.name ?? adj.module_slug} module`;
}

/** The adjustment's effect, e.g. "10% off", "£5.00 off", "Price set to £49.00". */
export function adjustmentValueLabel(adj: BillingAdjustment): string {
  switch (adj.adjustment_type) {
    case "percent":
      return `${(adj.value_bps ?? 0) / 100}% off`;
    case "fixed":
      return `${formatPence(adj.value_pence ?? 0)} off`;
    case "override":
      return `Price set to ${formatPence(adj.value_pence ?? 0)}`;
    default:
      return adj.adjustment_type;
  }
}

/** The adjustment's lifetime, e.g. "3 months (ends 2026-10-31)" or "Ongoing". */
export function adjustmentDurationLabel(adj: BillingAdjustment): string {
  if (adj.duration_type === "indefinite") return "Ongoing";
  if (adj.duration_type === "months") {
    const n = adj.duration_months ?? 0;
    return `${n} month${n === 1 ? "" : "s"}${adj.ends_on ? ` (ends ${adj.ends_on})` : ""}`;
  }
  return adj.ends_on ? `Until ${adj.ends_on}` : "Until its end date";
}

/**
 * Open an authenticated invoice-HTML endpoint in a new tab. A plain link
 * can't carry the bearer token, so fetch the document and open it as a blob.
 */
export async function openInvoiceHtml(api: ApiClient, path: string): Promise<void> {
  const response = await api.fetchRaw(path);
  if (!response.ok) {
    throw new Error(`Couldn't load the invoice (${response.status})`);
  }
  const html = await response.text();
  const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
  window.open(url, "_blank", "noopener");
  // Give the new tab time to load before releasing the object URL.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
